import { useToolsStore } from '../../store/toolsStore';
import { useTagsStore } from '../../store/tagsStore';
import { useState, useEffect, useRef, useCallback, type CSSProperties, type MouseEvent as ReactMouseEvent } from 'react';
import { useParams, useNavigate, useSearchParams, useBlocker } from 'react-router-dom';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toolsApi } from '../../lib/apiClient';
import { toast } from '../../lib/toast';
import { McpTool } from '../../types/types';
import { useAuthStore } from '../../store/authStore';
import { useConnectionsStore } from '../../store/connectionsStore';
import { Environment } from '../../types/environment';
import { hasMinRole } from '../../lib/permissions';
import { UserRole } from '../../enums/userRole';
import { safeJsonParse, safeJsonStringify, validateToolName } from '../../utils/security';
import { Download, Sparkles, SlidersHorizontal } from 'lucide-react';
import Checkbox from '../../ui/Checkbox/Checkbox';
import AiChatPanel from './aiChatPanel/AiChatPanel';
import WizardShell from './wizardShell/WizardShell';
import ToolInfoStep from './steps/ToolInfoStep';
import InputFieldsStep from './steps/InputFieldsStep';
import RequestMappingStep from './steps/RequestMappingStep';
import ChatExperienceStep from './steps/ChatExperienceStep';
import ResponseMappingStep from './steps/ResponseMappingStep';
import ReviewStep from './steps/ReviewStep';
import Loader from '../../ui/Loader/Loader';
import InputModal from '../../ui/inputModal/InputModal';
import { applyConnectorStateToToolSave } from './utils/toolSaveGuards';
import { buildQueryMapFromPath } from './hooks/useQueryMapBuilder';
import {
  DEFAULT_RESPONSE_TEMPLATE,
  NARROW_LAYOUT_QUERY,
  CHAT_PANE_MIN_WIDTH,
  CHAT_PANE_MAX_WIDTH,
  CHAT_PANE_HIDE_FIELDS_THRESHOLD,
  CHAT_PANE_HIDE_CHAT_THRESHOLD,
} from './constants';
import type { PaneMode } from '../../types/wizardTypes';
import styles from './ToolWizard.module.css';
import { useBackNavigation } from '../../hooks/useBackNavigation';

export default function ToolWizard() {
  const { tools, addTool, updateTool } = useToolsStore(s => s);
  const { getTags, setTags } = useTagsStore(s => s);
  const { id } = useParams();
  const navigate = useNavigate();
  const goBack = useBackNavigation('/toolslist');
  const [searchParams] = useSearchParams();
  const isNew = !id || id === 'new';

  const [tool, setTool] = useState<McpTool | null>(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const justSavedRef = useRef(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [confirmBackOpen, setConfirmBackOpen] = useState(false);
  const [aiMode, setAiMode] = useState(() => searchParams.get('ai') === 'true');
  // Below NARROW_LAYOUT_QUERY the split is too cramped, so only one pane is shown at a time
  const [isNarrowLayout, setIsNarrowLayout] = useState(false);
  const [paneMode, setPaneMode] = useState<PaneMode>('split');
  const [chatPaneWidth, setChatPaneWidth] = useState(30);
  const [isResizingChatPane, setIsResizingChatPane] = useState(false);
  const wizardUpdateRef = useRef<((patch: Record<string, unknown>) => void) | null>(null);
  const syncSchemaRef = useRef<((schema: string) => void) | null>(null);
  const navigateAfterSaveRef = useRef<string | null>(null); const skipBlockerRef = useRef(false);
  const splitBodyRef = useRef<HTMLDivElement | null>(null);
  const linkConversationRef = useRef<((toolId: number) => Promise<void>) | null>(null);

  const currentUser = useAuthStore(state => state.user);
  const canEdit = !!(currentUser && hasMinRole(currentUser?.role?.id, UserRole.ToolManager));
  const canView = canEdit || currentUser?.role?.id === UserRole.PromptEditor;
  const isSuperAdmin = !!(currentUser && hasMinRole(currentUser?.role?.id, UserRole.SuperAdmin));
  const connectors = useConnectionsStore(s => s.connectors);

  const goToStepRef = useRef<((index: number) => void) | null>(null);

  const toolSchema = z.object({
    name: z.string()
      .min(1, 'Name is required')
      .regex(/^[a-zA-Z0-9_]+$/, 'Name must contain only letters, numbers, and underscores')
      .superRefine((val, ctx) => {
        if (checkDuplicateName(val)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'A tool with this name already exists.',
          });
        }
      }),
    title: z.string().min(1, 'Title is required'),
    description: z.string().min(1, 'Description is required'),
    version: z.string().min(1, 'Version is required'),
    mainConnectionId: z.string({ invalid_type_error: 'Please select a system connection' }).min(1, 'Please select a system connection'),
    subConnectionsIds: z.array(z.string()).nullable().optional(),
    enabled: z.boolean().default(false),
    environment: z.enum(['prod', 'test', 'dev']).optional(),
    tags: z.array(z.object({
      id: z.number().nullable().optional(),
      name: z.string().min(1).max(100),
    })).optional(),
    httpMethod: z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'SELECT']).default('POST'),
    path: z.string().trim().default('/'),
    inputSchema: z.string().default(''),
    queryMap: z.array(z.object({
      key: z.string(),
      value: z.string(),
      selected: z.boolean().optional(),
      dynamic: z.boolean().optional(),
    })).default([]),
    headerMap: z.string().default(`{
      "Content-Type": "application/json",
      "Accept": "application/json"
    }`),
    bodyTemplate: z.string().default(''),
    responseTemplate: z.string().default(''),
    sampleResponse: z.string().default(''),
    annotations: z.string().default('{}'),
    formEnabled: z.boolean().default(false)
  });

  type ToolForm = z.infer<typeof toolSchema>;

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    setError,
    clearErrors,
    trigger,
    formState: { errors, submitCount, isDirty },
  } = useForm<ToolForm>({
    resolver: zodResolver(toolSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      enabled: false,
      version: '1.0.0',
      httpMethod: 'POST',
      tags: [],
      path: '/',
      queryMap: [],
      headerMap: `{
        "Content-Type": "application/json",
        "Accept": "application/json"
      }`,
      responseTemplate: DEFAULT_RESPONSE_TEMPLATE,
    },
  });

  const mapToolToFormValues = useCallback((toolData: McpTool): ToolForm => ({
    name: toolData.name ?? '',
    title: toolData.title ?? '',
    description: toolData.description ?? '',
    version: toolData.version ?? '1.0.0',
    mainConnectionId: toolData.mainConnectionId ?? '',
    subConnectionsIds: toolData.subConnectionsIds ?? [],
    enabled: toolData.enabled ?? false,
    environment: toolData.environment,
    tags: Array.isArray(toolData.tags) ? toolData.tags : [],
    httpMethod: toolData.httpMethod ?? 'POST',
    path: toolData.path ?? '/',
    inputSchema: safeJsonStringify(toolData.inputSchema, '{}'),
    queryMap: Array.isArray(toolData.queryMap) ? toolData.queryMap : [],
    headerMap: safeJsonStringify(toolData.headerMap, '{}'),
    bodyTemplate: toolData.bodyTemplate ?? '',
    responseTemplate: toolData.responseTemplate || DEFAULT_RESPONSE_TEMPLATE,
    sampleResponse: toolData.sampleResponse ?? '',
    annotations: safeJsonStringify(toolData.annotations, '{}'),
    formEnabled: toolData.formEnabled ?? false,
  }), []);

  useEffect(() => {
    if (!canView) {
      navigate('/toolslist');
    }
  }, [canView]);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && !skipBlockerRef.current && currentLocation.pathname !== nextLocation.pathname
  );

  useEffect(() => {
    if (blocker.state === 'blocked') {
      setConfirmBackOpen(true);
    }
  }, [blocker.state]);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    wizardUpdateRef.current = (patch: Record<string, unknown>) => {
      Object.entries(patch).forEach(([key, value]) => {
        if (key in toolSchema.shape) {
          if (key === 'inputSchema') {
            const schemaStr = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
            setValue('inputSchema', schemaStr, { shouldDirty: true });
            syncSchemaRef.current?.(schemaStr);
          } else if (key === 'queryMap') {
            // Convert whatever the AI returns into the required array format
            let rawItems: any[] = [];
            if (Array.isArray(value)) {
              rawItems = value;
            } else if (value && typeof value === 'object') {
              // AI returned {"key": "value"} object - convert to array
              rawItems = Object.entries(value as Record<string, string>).map(([k, v]) => ({
                key: k,
                value: typeof v === 'string' ? v : '',
              }));
            }
            const normalized = rawItems
              .filter((r: any) => r?.key)
              .map((r: any) => ({
                key: String(r.key),
                value: r.value != null ? String(r.value) : '',
                selected: r.selected !== false,   // default true so the row is visible
                dynamic: r.dynamic === true,
              }));
            const existing: any[] = watch('queryMap') || [];
            const normalizedByKey = new Map(normalized.map((r: any) => [r.key, r]));
            const updated = existing.map((r: any) => normalizedByKey.get(r.key) ?? r);
            const toAdd = normalized.filter((r: any) => !existing.some((e: any) => e.key === r.key));
            setValue('queryMap', [...updated, ...toAdd], { shouldDirty: true });
          } else if (key === 'headerMap') {
            const headerStr = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
            setValue('headerMap', headerStr, { shouldDirty: true });
          } else if (key === 'annotations') {
            const annotationsStr = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
            setValue('annotations', annotationsStr, { shouldDirty: true });
          } else if (key === 'path') {
            const newPath = value as string;
            const currentPath = (watch('path') as string) || '';
            const currentQueryString = currentPath.includes('?')
              ? currentPath.split('?').slice(1).join('?')
              : '';
            const pathToSet = !newPath.includes('?') && currentQueryString
              ? `${newPath}?${currentQueryString}`
              : newPath;
            setValue('path', pathToSet, { shouldDirty: true });
            if (!('queryMap' in patch)) {
              const currentQueryMap: any[] = watch('queryMap') || [];
              setValue('queryMap', buildQueryMapFromPath(pathToSet, currentQueryMap), { shouldDirty: true });
            }
          } else if (key === 'mainConnectionId') {
            setValue('mainConnectionId', value as any, { shouldDirty: true });
            // Keep httpMethod in sync with the connector type, same as the manual connector picker
            const willBeMssql = connectors.find(c => c.id === value)?.connectionTypeId === 7;
            if (willBeMssql) {
              setValue('httpMethod', 'SELECT', { shouldDirty: true });
            } else if (watch('httpMethod') === 'SELECT') {
              setValue('httpMethod', 'GET', { shouldDirty: true });
            }
          } else {
            setValue(key as keyof ToolForm, value as any, { shouldDirty: true });
          }
        }
      });
    };
  }, [setValue, watch, connectors]);

  const getCurrentValues = useCallback(() => ({
    name: watch('name'),
    title: watch('title'),
    description: watch('description'),
    httpMethod: watch('httpMethod'),
    path: watch('path'),
    mainConnectionId: watch('mainConnectionId'),
    inputSchema: watch('inputSchema'),
    bodyTemplate: watch('bodyTemplate'),
    headerMap: watch('headerMap'),
    queryMap: watch('queryMap'),
    annotations: watch('annotations'),
    formEnabled: watch('formEnabled'),
  }), [watch]);

  useEffect(() => {
    if (!isNew && id) {
      if (justSavedRef.current) {
        justSavedRef.current = false;
        return;
      }
      loadTool(parseInt(id));
    }
  }, [id, isNew, tools]);

  const loadTool = async (toolId: number) => {
    try {
      const toolData = tools.find(t => t.id === toolId);
      if (!toolData) return;

      setTool(toolData);
      reset(mapToolToFormValues(toolData));
    } catch (error: any) {
      toast.error(`Failed to load tool: ${error.message}`);
      navigate('/toolslist');
    } finally {
      setLoading(false);
    }
  };

  const checkDuplicateName = (name: string) => {
    if (!name) return false;
    const trimmed = name.trim();
    const currentId = tool?.id;
    return tools.some(t => t.name === trimmed && t.id !== currentId);
  };

  const goToFirstInvalidStep = (invalidStepIndexes: Set<number>) => {
    if (invalidStepIndexes.size === 0) return;
    // Reveal the wizard again, otherwise the user gets an error toast for a field they cannot see
    setPaneMode(mode => mode === 'chat' ? (isNarrowLayout ? 'fields' : 'split') : mode);
    if (!invalidStepIndexes.has(currentStepIndex)) {
      const firstInvalidStep = Math.min(...Array.from(invalidStepIndexes));
      goToStepRef.current?.(firstInvalidStep);
    }
  };

  const onSubmit = async (data: ToolForm) => {
    const nameValidation = validateToolName(data.name);
    if (!nameValidation.valid) {
      setError('name', { type: 'manual', message: nameValidation.error || 'Invalid tool name' });
      goToFirstInvalidStep(new Set([0]));
      return;
    }

    if (checkDuplicateName(data.name)) {
      setError('name', { type: 'manual', message: 'A tool with this name already exists.' });
      goToFirstInvalidStep(new Set([0]));
      return;
    }

    setSaving(true);
    try {
      const inputSchemaResult = safeJsonParse(data.inputSchema || '{}', {});
      if (!inputSchemaResult.success) {
        setError('inputSchema', { type: 'manual', message: 'Invalid input schema format' });
        goToFirstInvalidStep(new Set([2]));
        toast.error('Invalid input schema format. Please check your input fields configuration.');
        setSaving(false);
        return;
      }

      const headerMapResult = safeJsonParse(data.headerMap || '{}', {});
      if (!headerMapResult.success) {
        setError('headerMap', { type: 'manual', message: 'Invalid header configuration' });
        goToFirstInvalidStep(new Set([1]));
        toast.error('Invalid header configuration.');
        setSaving(false);
        return;
      }

      const annotationsResult = safeJsonParse(data.annotations || '{}', {});
      if (!annotationsResult.success) {
        setError('annotations', { type: 'manual', message: 'Invalid chat experience configuration' });
        goToFirstInvalidStep(new Set([3]));
        toast.error('Invalid chat experience configuration.');
        setSaving(false);
        return;
      }

      const toolData = applyConnectorStateToToolSave({
        ...data,
        mainConnectionId: data.mainConnectionId || null,
        inputSchema: inputSchemaResult.data,
        queryMap: data.queryMap || [],
        headerMap: headerMapResult.data,
        annotations: annotationsResult.data,
      }, connectors);

      if (data.enabled && !toolData.enabled) {
        setValue('enabled', false, { shouldDirty: true, shouldValidate: true });
      }

      if (isNew) {
        const newTool = await toolsApi.create(toolData) as McpTool;
        toast.success('Tool created successfully!');
        await linkConversationRef.current?.(newTool.id!);
        setTool(newTool);
        addTool(newTool);
        justSavedRef.current = true;
        reset(mapToolToFormValues(newTool));
        const dest = navigateAfterSaveRef.current;
        navigateAfterSaveRef.current = null;
        skipBlockerRef.current = true;
        if (dest) {
          navigate(dest);
        } else {
          navigate(`/toolslist/${newTool.id}?ai=true`, { replace: true });
        }
      } else {
        const updatedTool = await toolsApi.update(parseInt(id!), toolData);
        justSavedRef.current = true;
        setTool(updatedTool.tool);
        updateTool(updatedTool.tool);
        reset(mapToolToFormValues(updatedTool.tool));
        toast.success('Tool updated successfully!');
        const dest = navigateAfterSaveRef.current;
        navigateAfterSaveRef.current = null;
        if (dest) {
          skipBlockerRef.current = true;
          navigate(dest);
        }
      }
      getTags().then(fetched => { if (fetched) setTags(fetched) });
    } catch (error: any) {
      navigateAfterSaveRef.current = null;
      toast.error(`Failed to save tool: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const responseStepOffset = isSuperAdmin ? 1 : 0;
  const fieldToStep: Partial<Record<keyof ToolForm, number>> = {
    name: 0,
    title: 0,
    description: 0,
    version: 0,
    mainConnectionId: 0,
    httpMethod: 1,
    path: 1,
    bodyTemplate: 1,
    headerMap: 1,
    inputSchema: 2,
    formEnabled: 2,
    responseTemplate: isSuperAdmin ? 3 : undefined,
    annotations: 3 + responseStepOffset,
  };

  const getErrorStepIndexes = (formErrors: FieldErrors<ToolForm>) => {
    const steps = new Set<number>();
    Object.keys(formErrors).forEach((key) => {
      const stepIndex = fieldToStep[key as keyof ToolForm];
      if (stepIndex !== undefined) {
        steps.add(stepIndex);
      }
    });
    return steps;
  };

  const onInvalidSubmit = (formErrors: FieldErrors<ToolForm>) => {
    navigateAfterSaveRef.current = null;
    toast.error('Please fill all required fields before saving.');
    const invalidStepIndexes = getErrorStepIndexes(formErrors);
    goToFirstInvalidStep(invalidStepIndexes);
  };

  useEffect(() => {
    const mq = window.matchMedia(NARROW_LAYOUT_QUERY);
    const handler = (e: MediaQueryListEvent | MediaQueryList) => setIsNarrowLayout(e.matches);
    handler(mq);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // Narrow viewports cannot show both panes, so the split collapses to the chat and back again
  useEffect(() => {
    setPaneMode(mode => isNarrowLayout ? (mode === 'split' ? 'chat' : mode) : 'split');
  }, [isNarrowLayout]);

  useEffect(() => {
    if (!isResizingChatPane) return;

    const handleMouseMove = (event: MouseEvent) => {
      const splitBody = splitBodyRef.current;
      if (!splitBody) return;

      const { left, width } = splitBody.getBoundingClientRect();
      if (width <= 0) return;

      const nextWidth = ((event.clientX - left) / width) * 100;

      // Dragging well past a bound is a request for a single pane, not a lopsided split
      if (nextWidth > CHAT_PANE_HIDE_FIELDS_THRESHOLD) {
        setPaneMode('chat');
        setIsResizingChatPane(false);
        return;
      }

      if (nextWidth < CHAT_PANE_HIDE_CHAT_THRESHOLD) {
        setPaneMode('fields');
        setIsResizingChatPane(false);
        return;
      }

      const clampedWidth = Math.min(CHAT_PANE_MAX_WIDTH, Math.max(CHAT_PANE_MIN_WIDTH, nextWidth));
      setChatPaneWidth(clampedWidth);
    };

    const handleMouseUp = () => setIsResizingChatPane(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizingChatPane]);

  const handleChatPaneResizeStart = useCallback((event: ReactMouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsResizingChatPane(true);
  }, []);

  // Collapsing only applies while the AI chat is open — otherwise the wizard is already full width
  const collapsible = canEdit && aiMode;
  const fieldsHidden = collapsible && paneMode === 'chat';
  const chatHidden = collapsible && paneMode === 'fields';
  const isCollapsed = fieldsHidden || chatHidden;

  // A wide viewport can show both panes, so its pills restore the split; a narrow one swaps panes
  const restorePaneMode: PaneMode = isNarrowLayout ? 'chat' : 'split';

  const handleAiButtonClick = () => {
    if (chatHidden) {
      setPaneMode(restorePaneMode);
      return;
    }
    if (!aiMode) setPaneMode(restorePaneMode);
    setAiMode(m => !m);
  };

  // Mirror of the AI button: toggles its own pane, restoring the split when the viewport can fit both
  const handleFieldsButtonClick = () => {
    if (fieldsHidden) {
      setPaneMode(isNarrowLayout ? 'fields' : 'split');
      return;
    }
    setPaneMode('chat');
  };

  const splitBodyStyle = aiMode
    ? ({ ['--chat-pane-width' as '--chat-pane-width']: fieldsHidden ? '100%' : `${chatPaneWidth}%` } as CSSProperties)
    : undefined;
  const bodyClassName = canEdit
    ? [
      styles.splitBody,
      aiMode ? styles.aiOpen : '',
      fieldsHidden ? styles.fieldsHidden : '',
      chatHidden ? styles.chatHidden : '',
    ].filter(Boolean).join(' ')
    : styles.fullBody;

  if (loading) {
    return (
      <div className={styles.loading}>
        <Loader />
        <span>Loading tool...</span>
      </div>
    );
  }

  const mainConnectionId = watch('mainConnectionId');
  const currentConnection = connectors.find(c => c.id === mainConnectionId);
  const isToolDisabledByConnector = !(currentConnection && currentConnection.isActive);
  const currentEnv = currentConnection?.environment as Environment | undefined;
  const errorStepIndexes = submitCount > 0 ? getErrorStepIndexes(errors) : new Set<number>();

  const handleSave = () => {
    handleSubmit(onSubmit, onInvalidSubmit)();
  };

  const handleExport = async () => {
    if (!tool?.id) return;
    try {
      const data = await toolsApi.exportPackage([tool.id]);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const safeName = (data.package?.name || tool.name || 'tool').replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
      a.download = `${safeName}-v${data.package?.version || '1.0.0'}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Tool exported successfully');
    } catch (error: any) {
      toast.error(`Failed to export tool: ${error.message}`);
    }
  };

  const aiButtonLabel = chatHidden
    ? 'AI Chat'
    : aiMode ? 'Close AI' : isNew ? 'Build with AI' : 'Edit with AI';
  const fieldsButtonLabel = fieldsHidden ? 'Tool Fields' : 'Hide Fields';

  return (
    <div className={`${styles.container} ${aiMode ? styles.aiMode : ''}`}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {currentConnection && currentEnv && (
            <span className={`${styles.envTag} ${styles[`env${currentEnv}`]}`}>
              {currentEnv === 'test' ? 'Test' : currentEnv === 'dev' ? 'Dev' : 'Prod'}
            </span>
          )}
          <h1 className={styles.title}>
            {isNew ? 'Create New Tool' : `Tool: ${tool?.title || tool?.name || ''}`}
          </h1>
        </div>
        <div className={styles.headerActions}>
          <button
            onClick={() => isDirty ? setConfirmBackOpen(true) : goBack()}
            className={styles.cancelBtn}
            type="button"
          >
            ← Back
          </button> {!isNew && tool?.id && (
            <button
              type="button"
              className={styles.exportBtn}
              onClick={handleExport}
              title="Export tool as JSON"
            >
              <Download size={16} />
            </button>
          )}
          <div className={styles.enabledToggle}>
            <label className={styles.headerCheckboxLabel}>
              <Checkbox
                disabled={!canEdit}
                checked={watch('enabled')}
                onChange={e => {
                  if (!mainConnectionId) {
                    toast.info('Please select a connection before enabling this tool');
                    return;
                  }
                  if (isToolDisabledByConnector) {
                    return toast.error('Cannot enable tool because its connection is inactive.');
                  }
                  setValue('enabled', e, { shouldDirty: true, shouldValidate: true });
                }}
              />
              <span className={styles.toggleText}>Enabled</span>
            </label>
          </div>

          {canEdit && (
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={saving || (!isDirty && !isNew)}
            >
              {saving ? 'Saving...' : isNew ? 'Create Tool' : 'Save Tool'}
            </button>
          )}

        </div>
      </div>

      <div
        ref={splitBodyRef}
        className={bodyClassName}
        style={splitBodyStyle}
      >
        {canEdit && !fieldsHidden && (
          <div className={styles.aiToggleDock}>
            <button
              type="button"
              className={`${styles.aiBtn} ${aiMode && !chatHidden ? styles.aiBtnActive : ''}`}
              onClick={handleAiButtonClick}
              aria-label={aiButtonLabel}
              title={aiButtonLabel}
            >
              <span className={styles.aiBtnFace}>
                <Sparkles size={16} className={styles.aiBtnIcon} />
                <span className={styles.aiBtnText}>{aiButtonLabel}</span>
              </span>
            </button>
          </div>
        )}
        {canEdit && aiMode && !chatHidden && (
          <div className={styles.fieldsToggleDock}>
            <button
              type="button"
              className={`${styles.aiBtn} ${styles.fieldsBtn} ${fieldsHidden ? '' : styles.aiBtnActive}`}
              onClick={handleFieldsButtonClick}
              aria-label={fieldsButtonLabel}
              title={fieldsButtonLabel}
            >
              <span className={styles.aiBtnFace}>
                <SlidersHorizontal size={16} className={styles.aiBtnIcon} />
                <span className={styles.aiBtnText}>{fieldsButtonLabel}</span>
              </span>
            </button>
          </div>
        )}
        {canEdit && (
          <>
            <div
              className={styles.chatPane}
              aria-hidden={!aiMode}
            >
              <AiChatPanel
                wizardUpdateRef={wizardUpdateRef}
                toolId={tool?.id ?? null}
                toolTitle={tool?.title || tool?.name}
                isNew={isNew}
                getCurrentValues={getCurrentValues}
                connectors={connectors.filter(c => c.isActive)}
                checkDuplicateName={checkDuplicateName}
                linkConversationRef={linkConversationRef}
              />
            </div>
            <div
              className={styles.resizeHandle}
              onMouseDown={aiMode && !isCollapsed ? handleChatPaneResizeStart : undefined}
              role="separator"
              aria-orientation="vertical"
              aria-label="Resize AI chat panel"
              aria-valuemin={CHAT_PANE_MIN_WIDTH}
              aria-valuemax={CHAT_PANE_MAX_WIDTH}
              aria-valuenow={Math.round(chatPaneWidth)}
            />
          </>
        )}
        <div className={canEdit ? styles.wizardPane : styles.fullPane}>
          <WizardShell
            watch={watch}
            setValue={setValue}
            goToStepRef={goToStepRef}
            onStepChange={setCurrentStepIndex}
            errorStepIndexes={errorStepIndexes}
            isSuperAdmin={isSuperAdmin}
          >
            {(stepId) => {
              const stepProps = { watch, setValue, register, errors, canEdit, setError, clearErrors, trigger };
              switch (stepId) {
                case 'tool-info':
                  return (
                    <ToolInfoStep
                      {...stepProps}
                      checkDuplicateName={checkDuplicateName}
                    />
                  );
                case 'request-mapping':
                  return <RequestMappingStep {...stepProps} />;
                case 'input-fields':
                  return <InputFieldsStep {...stepProps} syncSchemaRef={syncSchemaRef} />;
                case 'response-mapping':
                  return <ResponseMappingStep {...stepProps} />;
                case 'chat-quick-actions':
                  return <ChatExperienceStep {...stepProps} />;
                case 'review':
                  return <ReviewStep {...stepProps} onSave={handleSubmit(onSubmit)} saving={saving} isNew={isNew} />;
                default:
                  return null;
              }
            }}
          </WizardShell>
        </div>
      </div>
      <InputModal
        open={confirmBackOpen}
        title="Unsaved changes"
        message={`You have unsaved changes. ${isNew ? 'Create the tool before leaving, or discard your work?' : 'Save before leaving, or discard your changes?'}`}
        showInput={false}
        confirmText={isNew ? 'Create Tool' : 'Save Tool'}
        cancelText="Stay"
        secondaryText="Discard"
        onConfirm={() => {
          setConfirmBackOpen(false);
          const dest = blocker.state === 'blocked'
            ? blocker.location.pathname + blocker.location.search + blocker.location.hash
            : '/toolslist';
          blocker.reset?.();
          navigateAfterSaveRef.current = dest;
          handleSave();
        }}
        onCancel={() => {
          setConfirmBackOpen(false);
          blocker.reset?.();
        }}
        onSecondary={() => {
          setConfirmBackOpen(false);
          if (blocker.state === 'blocked') {
            blocker.proceed?.();
          } else {
             skipBlockerRef.current = true;
             goBack();
          }
        }}
      />
    </div>
  );
}
